import { Stack } from "@mui/material";
import { type ExternalAccountLite } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { Button } from "@src/appV2/redesign/components/Button";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Illustration, LoadingSpinner, Text } from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";

import { ExternalAccountErrorCard } from "./ExternalAccountErrorCard";

export const FAILED_PAYOUT_PAGE_ACTIONS = [
  "retry-payout",
  "update-payout-settings",
  "talk-to-support",
] as const;

export type FailedPayoutPageAction = (typeof FAILED_PAYOUT_PAGE_ACTIONS)[number];

export interface FailedPayoutPageContentProps {
  errorTitle: string;
  errorDescription: string;
  errorTitleShort: string;
  externalAccount: ExternalAccountLite;
  actions: FailedPayoutPageAction[];
  isRetryPayoutLoading?: boolean;
  onRetryPayout?: () => void;
  onOpenPayoutSettings?: () => void;
  onTalkToSupport?: () => void;
}

export function FailedPayoutPageContent(props: FailedPayoutPageContentProps) {
  const {
    errorTitle,
    errorDescription,
    errorTitleShort,
    externalAccount,
    actions,
    isRetryPayoutLoading,
    onRetryPayout,
    onOpenPayoutSettings,
    onTalkToSupport,
  } = props;

  return (
    <Stack
      sx={(theme) => ({
        flex: 1,
        overflow: "hidden",
        ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
      })}
    >
      <Stack
        sx={(theme) => ({
          flex: 1,
          overflow: "auto",
          paddingTop: theme.spacing(5),
          paddingX: theme.spacing(5),
        })}
      >
        <Illustration
          type={externalAccount.type === "card" ? "error-credit-card" : "error-bank"}
          sx={{ alignSelf: "center" }}
        />
        <Text variant="h2" sx={(theme) => ({ textAlign: "center", marginTop: theme.spacing(7) })}>
          {errorTitle}
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({ textAlign: "center", marginTop: theme.spacing(7) })}
        >
          {errorDescription}
        </Text>
        <ExternalAccountErrorCard
          errorMessage={errorTitleShort}
          externalAccount={externalAccount}
          sx={(theme) => ({ marginTop: theme.spacing(8) })}
        />
      </Stack>
      <Stack
        spacing={4}
        sx={(theme) => ({
          paddingTop: theme.spacing(4),
          paddingX: theme.spacing(5),
          paddingBottom: theme.spacing(3),
          alignItems: "center",
        })}
      >
        {actions.includes("retry-payout") ? (
          <Button fullWidth onClick={onRetryPayout}>
            {isRetryPayoutLoading ? (
              <LoadingSpinner size="small" variant="invert" />
            ) : (
              "Retry payout"
            )}
          </Button>
        ) : null}
        {actions.includes("update-payout-settings") ? (
          <Button
            fullWidth
            variant={actions.includes("retry-payout") ? "outlined" : "contained"}
            onClick={onOpenPayoutSettings}
          >
            Update payout settings
          </Button>
        ) : null}
        {actions.includes("talk-to-support") ? (
          <TextButton underline onClick={onTalkToSupport}>
            Talk to support
          </TextButton>
        ) : null}
      </Stack>
    </Stack>
  );
}
